import { Fragment, useRef } from "react";

import {
  DATE_OF_BIRTH_PARTS,
  isPartComplete,
  keepDigits,
  maxLengthOf,
  splitDateText,
} from "features/comunications/utils/dateOfBirthEntry";

import styles from "./DateOfBirthInput.module.css";

const PART_DETAILS = {
  day: { label: "Day", placeholder: "DD", autoComplete: "bday-day" },
  month: { label: "Month", placeholder: "MM", autoComplete: "bday-month" },
  year: { label: "Year", placeholder: "YYYY", autoComplete: "bday-year" },
};

/*
 * One box reading DD / MM / YYYY, built from three inputs so each part keeps its own value and
 * label. The cursor moves on when a part is full or the patient types "/", and Backspace in an
 * empty part moves back and deletes the digit before it, so it types like a single field.
 */
export const DateOfBirthInput = ({
  id,
  value,
  onChange,
  onBlur,
  hasError = false,
  describedBy,
}) => {
  const inputRefs = useRef({});

  const focusPart = (index) => {
    const input = inputRefs.current[DATE_OF_BIRTH_PARTS[index]];

    if (!input) return;

    input.focus();
    input.select();
  };

  const handleChange = (part, index) => (e) => {
    const pastedDate = splitDateText(e.target.value);

    if (pastedDate) {
      onChange(pastedDate);
      focusPart(DATE_OF_BIRTH_PARTS.length - 1);

      return;
    }

    const digits = keepDigits({ part, value: e.target.value });

    onChange({ ...value, [part]: digits });

    if (digits.length > value[part].length && isPartComplete({ part, value: digits })) {
      focusPart(index + 1);
    }
  };

  const handleKeyDown = (part, index) => (e) => {
    if (e.key === "/") {
      e.preventDefault();

      if (value[part]) focusPart(index + 1);

      return;
    }

    if (e.key === "Backspace" && !value[part] && index > 0) {
      const previousPart = DATE_OF_BIRTH_PARTS[index - 1];

      e.preventDefault();
      onChange({ ...value, [previousPart]: value[previousPart].slice(0, -1) });
      inputRefs.current[previousPart]?.focus();
    }
  };

  const focusFirstEmptyPart = (e) => {
    if (e.target.tagName === "INPUT") return;

    const firstEmpty = DATE_OF_BIRTH_PARTS.findIndex((part) => !value[part]);

    focusPart(firstEmpty === -1 ? DATE_OF_BIRTH_PARTS.length - 1 : firstEmpty);
  };

  return (
    // The box itself is not interactive; a click on its padding or a slash goes to the input.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`${styles.box} ${hasError ? styles.boxError : ""}`}
      onClick={focusFirstEmptyPart}
    >
      {DATE_OF_BIRTH_PARTS.map((part, index) => {
        const { label, placeholder, autoComplete } = PART_DETAILS[part];

        return (
          <Fragment key={part}>
            {index > 0 && (
              <span className={styles.separator} aria-hidden="true">
                /
              </span>
            )}
            <input
              ref={(element) => {
                inputRefs.current[part] = element;
              }}
              id={index === 0 ? id : undefined}
              className={`${styles.part} ${part === "year" ? styles.yearPart : ""}`}
              type="text"
              inputMode="numeric"
              autoComplete={autoComplete}
              aria-label={label}
              aria-invalid={hasError || undefined}
              aria-describedby={describedBy}
              placeholder={placeholder}
              maxLength={part === "day" ? 10 : maxLengthOf(part)}
              value={value[part]}
              onChange={handleChange(part, index)}
              onKeyDown={handleKeyDown(part, index)}
              onBlur={() => onBlur(part)}
            />
          </Fragment>
        );
      })}
    </div>
  );
};
